import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getApiErrorMessage } from '../api';
import ModalShell from '../components/ModalShell';
import { ActionMenu, ActionMenuItem } from '../components/ActionMenu';
import { normalizeFieldText } from '../utils/textCase';
import { INVITE_EMAIL_BODY, INVITE_EMAIL_SUBJECT } from '../constants/portalBrand';
import MultiSelectFilter from '../components/MultiSelectFilter';
import QuickFilterGrid from '../components/QuickFilterGrid';
import ListFilterBar from '../components/ListFilterBar';
import FilterSearchField from '../components/FilterSearchField';
import { matchesOneOf } from '../utils/listFilters';
import SortableTh from '../components/SortableTh';
import { useSortableTable } from '../utils/sortableTable';
import { RECRUITER_SORT_COLUMNS } from '../constants/listTableSort';
import TableExportButton from '../components/TableExportButton';
import { recruiterColumns, mapRecruiters } from '../utils/dashboardColumns';

const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

export default function Recruiters({
  embedded = false,
  embeddedEditId = null,
  initialRecruiter = null,
  onEmbeddedClose = null
}) {
  const embeddedInitRef = useRef(false);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ 
    queryKey: ['recruiters'], 
    queryFn: async () => {
      try {
        const response = await api.get('/recruiters');
        return response.data || [];
      } catch (err) {
        console.error('Error fetching recruiters:', err);
        // Return empty array instead of throwing to prevent UI crash
        if (err.response?.status === 404) {
          return [];
        }
        throw err;
      }
    },
    retry: 1,
    enabled: true
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', status: 'active', password: '', sendInvite: false });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const filteredRecruiters = useMemo(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();
    return data.filter(r => {
        const status = (r.status || 'active').toLowerCase();
        if (!matchesOneOf(statusFilters, status)) return false;
        if (!q) return true;
        const haystack = [r.id, r.name, r.email, r.phone, r.status, r.userId]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
  }, [data, searchQuery, statusFilters]);

  const { sort, toggleSort, sortedRows: sortedRecruiters } = useSortableTable(
    filteredRecruiters,
    RECRUITER_SORT_COLUMNS
  );

  const hasActiveFilters = Boolean(searchQuery.trim() || statusFilters.length > 0);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilters([]);
  };
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [emailDraft, setEmailDraft] = useState({
    subject: INVITE_EMAIL_SUBJECT,
    body: INVITE_EMAIL_BODY
  });

  const createRecruiter = useMutation({
    mutationFn: payload => api.post('/recruiters', {
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      status: payload.status,
      password: payload.password || 'password123',
      sendInvite: !!payload.sendInvite
    }).then(r => r.data),
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: ['recruiters'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      if (data.inviteSent && data.user?.password) {
        alert(
          `Recruiter created! Login credentials:\nEmail: ${data.user.email}\nPassword: ${data.user.password}\n\nApplication URL: ${window.location.origin}/login\n\n(Email invite would be sent in production — use the draft above to copy and send.)`
        );
      } else if (data.user?.email) {
        alert(
          `Recruiter created!\n\nThey can log in with:\nEmail: ${data.user.email}\nPassword: (the initial password you set)\n\nApplication URL: ${window.location.origin}/login`
        );
      }
      setForm({ name: '', email: '', phone: '', status: 'active', password: 'password123', sendInvite: false });
      if (embedded) closeRecruiterModal();
      else {
        setShowModal(false);
        setEditing(null);
      }
    },
    onError: (error) => {
      console.error('Error creating recruiter:', error);
    }
  });

  const updateRecruiter = useMutation({
    mutationFn: payload => {
      const body = {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        status: payload.status,
        sendInvite: !!payload.sendInvite
      };
      if (payload.password?.trim()) body.password = payload.password.trim();
      return api.put(`/recruiters/${payload.id}`, body).then(r => r.data);
    },
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: ['recruiters'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      if (data.inviteSent && data.user?.password) {
        alert(
          `Recruiter updated.\n\nNew login password:\nEmail: ${data.user.email}\nPassword: ${data.user.password}\n\nApplication URL: ${window.location.origin}/login`
        );
      }
      setShowModal(false);
      setEditing(null);
      if (embedded && onEmbeddedClose) onEmbeddedClose();
    },
    onError: (error) => {
      console.error('Error updating recruiter:', error);
    }
  });

  const changePassword = useMutation({
    mutationFn: ({ id, password }) => api.put(`/recruiters/${id}`, { password }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recruiters'] });
      setShowPasswordModal(null);
      setNewPassword('');
      alert('Password changed successfully!');
    },
    onError: (error) => {
      console.error('Error changing password:', error);
      alert('Error changing password: ' + (error.response?.data?.message || error.message));
    }
  });

  const deleteRecruiter = useMutation({
    mutationFn: id => api.delete(`/recruiters/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recruiters'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
    }
  });

  const openModal = (recruiter = null) => {
    setEditing(recruiter);
    if (recruiter) {
      setForm({
        name: recruiter.name || '',
        email: recruiter.email || '',
        phone: recruiter.phone || '',
        status: recruiter.status || 'active',
        password: '',
        sendInvite: false
      });
    } else {
      setForm({ name: '', email: '', phone: '', status: 'active', password: 'password123', sendInvite: false });
    }
    setShowModal(true);
  };

  const closeRecruiterModal = () => {
    setShowModal(false);
    setEditing(null);
    if (embedded && onEmbeddedClose) onEmbeddedClose();
  };

  useEffect(() => {
    if (!embedded || !embeddedEditId || embeddedInitRef.current) return;
    const recruiter =
      initialRecruiter?.id === embeddedEditId
        ? initialRecruiter
        : data?.find(r => r.id === embeddedEditId);
    if (recruiter) {
      embeddedInitRef.current = true;
      openModal(recruiter);
    }
  }, [embedded, embeddedEditId, initialRecruiter, data]);

  if (isLoading) return <div className="p-6">Loading...</div>;
  
  if (error && error.response?.status !== 404) {
    return (
      <div className="p-6">
        <div className="text-red-600">{getApiErrorMessage(error, 'Error loading recruiters. Please try again.')}</div>
        <button 
          className="mt-4 px-4 py-2 border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))]" 
          onClick={() => qc.invalidateQueries({ queryKey: ['recruiters'] })}
        >
          Retry
        </button>
      </div>
    );
  }

  const submitForm = () => {
    // Validate required fields
    if (!form.name || !form.email) {
      alert('Name and email are required');
      return;
    }
    
    if (editing) {
      updateRecruiter.mutate({
        id: editing.id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone,
        status: form.status,
        password: form.password,
        sendInvite: form.sendInvite
      });
    } else {
      if (!form.password?.trim()) {
        alert('Please set an initial password for the recruiter login.');
        return;
      }
      createRecruiter.mutate(form);
    }
  };

  if (embedded && !showModal) {
    return null;
  }

  return (
    <div className={embedded ? '' : 'space-y-4'}>
      {!embedded && (
      <>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Recruiters</h1>
        <div className="flex items-center gap-2">
          <TableExportButton
            filename="recruiters"
            sheetName="Recruiters"
            columns={recruiterColumns}
            rows={mapRecruiters(sortedRecruiters)}
          />
          <button className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 text-sm font-medium" onClick={() => openModal()}>
            Add recruiter
          </button>
        </div>
      </div>

      <ListFilterBar>
        <QuickFilterGrid
          search={
            <FilterSearchField
              placeholder="Search by ID, name, email, phone..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          }
          filters={[
            <MultiSelectFilter
              key="status"
              options={STATUS_FILTER_OPTIONS}
              values={statusFilters}
              onChange={setStatusFilters}
              emptyLabel="All statuses"
            />
          ]}
        />
        {hasActiveFilters && (
          <div className="mt-2 flex items-center justify-between text-xs text-[hsl(var(--ds-muted-fg))]">
            <span>
              Showing {sortedRecruiters.length} of {data?.length || 0} recruiters
            </span>
            <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </ListFilterBar>

      <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          {(!data || data.length === 0) ? (
            <div className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">No recruiters found</div>
          ) : sortedRecruiters.length === 0 ? (
            <div className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
              No recruiters match your search or filters
            </div>
          ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-2 whitespace-nowrap">Sl No</th>
                <SortableTh label="Recruiter ID" sortKey="id" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                <SortableTh label="Email" sortKey="email" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                <SortableTh label="Phone" sortKey="phone" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                <th className="text-left px-4 py-2 sticky right-0 bg-[hsl(var(--ds-muted))] z-10 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
                {sortedRecruiters.map((recruiter, idx) => (
                  <tr key={recruiter.id} className="border-b border-[hsl(var(--ds-border))] h-16">
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))]">{recruiter.id}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="font-semibold text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 text-left"
                        onClick={() => openModal(recruiter)}
                      >
                        {recruiter.name}
                      </button>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{recruiter.email}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{recruiter.phone || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs ${recruiter.status === 'inactive' ? 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]' : 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]'}`}>
                        {recruiter.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right sticky right-0 bg-[hsl(var(--ds-surface))] whitespace-nowrap">
                      <ActionMenu size="md">
                        <ActionMenuItem onClick={() => openModal(recruiter)}>Edit</ActionMenuItem>
                        <ActionMenuItem onClick={() => setShowPasswordModal(recruiter)}>Change Password</ActionMenuItem>
                        <ActionMenuItem onClick={() => updateRecruiter.mutate({ ...recruiter, status: 'inactive' })}>Deactivate</ActionMenuItem>
                        <ActionMenuItem danger onClick={() => { if (window.confirm('Are you sure you want to delete this recruiter?')) deleteRecruiter.mutate(recruiter.id); }}>Delete</ActionMenuItem>
                      </ActionMenu>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          )}
        </div>
      </div>
      </>
      )}

      {showModal && (
        <ModalShell
          title={editing ? 'Edit recruiter' : 'Add recruiter'}
          onClose={closeRecruiterModal}
          maxWidth="max-w-xl"
          zIndex={embedded ? 'z-[14000]' : undefined}
          curvy
          footer={
            <>
              {(createRecruiter.isError || updateRecruiter.isError) && (
                <span className="text-sm text-red-600 mr-auto">
                  {createRecruiter.error?.response?.data?.message || updateRecruiter.error?.response?.data?.message || 'Error saving recruiter'}
                </span>
              )}
              <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={closeRecruiterModal}>Cancel</button>
              <button type="button" className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium disabled:opacity-50" onClick={submitForm} disabled={createRecruiter.isLoading || updateRecruiter.isLoading}>
                {editing ? 'Save changes' : 'Add recruiter'}
              </button>
            </>
          }
        >
            <div className="grid gap-3 md:grid-cols-2">
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.name} onChange={e => setForm(f => ({ ...f, name: normalizeFieldText(e.target.value, 'name') }))} />
                <label>Name</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" type="email" placeholder=" " value={form.email} onChange={e => setForm(f => ({ ...f, email: normalizeFieldText(e.target.value, 'email') }))} />
                <label>Email (login)</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.phone} onChange={e => setForm(f => ({ ...f, phone: normalizeFieldText(e.target.value, 'phone') }))} />
                <label>Phone</label>
              </div>
              <div className="field-with-label">
                <select className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <label>Status</label>
              </div>
              {!editing ? (
                <>
                  <div className="field-with-label">
                    <input
                      className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                      type="password"
                      placeholder=" "
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    />
                    <label>Initial Password</label>
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendInviteRecruiter"
                      checked={form.sendInvite}
                      onChange={e => setForm(f => ({ ...f, sendInvite: e.target.checked }))}
                    />
                    <label htmlFor="sendInviteRecruiter" className="text-sm">
                      Send email invite with login credentials
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="field-with-label">
                    <input
                      className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                      type="password"
                      placeholder=" "
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    />
                    <label>New Password (optional)</label>
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendInviteRecruiterEdit"
                      checked={form.sendInvite}
                      onChange={e => setForm(f => ({ ...f, sendInvite: e.target.checked }))}
                    />
                    <label htmlFor="sendInviteRecruiterEdit" className="text-sm">
                      Send email invite with updated credentials
                    </label>
                  </div>
                  <p className="md:col-span-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                    Login email is the Email field above. Use Change Password from the row menu, or set a new password here.
                  </p>
                </>
              )}
            </div>
            {form.sendInvite && (
              <div className="bg-[hsl(var(--ds-primary)/0.06)] border border-[hsl(var(--ds-primary)/0.25)] p-3 text-sm space-y-2 mt-3">
                <div className="font-semibold">Email Draft (Editable):</div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium block mb-1">To:</label>
                    <input type="email" className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full bg-[hsl(var(--ds-surface))]" value={form.email} readOnly />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Subject:</label>
                    <input
                      type="text"
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full"
                      value={emailDraft.subject}
                      onChange={e => setEmailDraft(d => ({ ...d, subject: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Body:</label>
                    <textarea
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full"
                      rows={8}
                      value={emailDraft.body
                        .replace('[Name]', form.name || '[Name]')
                        .replace('[Email]', form.email || '[Email]')
                        .replace('[Password]', form.password || '[Password]')
                        .replace('[ApplicationURL]', `${window.location.origin}/login`)}
                      onChange={e => {
                        const newBody = e.target.value
                          .replace(form.name || '[Name]', '[Name]')
                          .replace(form.email || '[Email]', '[Email]')
                          .replace(form.password || '[Password]', '[Password]')
                          .replace(`${window.location.origin}/login`, '[ApplicationURL]')
                          .replace(window.location.origin, '[ApplicationURL]');
                        setEmailDraft(d => ({ ...d, body: newBody }));
                      }}
                    />
                    <div className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                      Placeholders: [Name], [Email], [Password], [ApplicationURL]
                    </div>
                  </div>
                </div>
              </div>
            )}
        </ModalShell>
      )}

      {showPasswordModal && (
        <ModalShell
          title="Change Password"
          subtitle={showPasswordModal.name}
          onClose={() => { setShowPasswordModal(null); setNewPassword(''); }}
          maxWidth="max-w-md"
          footer={
            <>
              <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={() => { setShowPasswordModal(null); setNewPassword(''); }}>Cancel</button>
              <button
                type="button"
                className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium disabled:opacity-60"
                onClick={() => changePassword.mutate({ id: showPasswordModal.id, password: newPassword })}
                disabled={!newPassword || changePassword.isLoading}
              >
                {changePassword.isLoading ? 'Changing...' : 'Change Password'}
              </button>
            </>
          }
        >
              <div className="text-sm text-[hsl(var(--ds-muted-fg))] mb-3">{showPasswordModal.email}</div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" type="password" placeholder=" " value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                <label>New Password</label>
              </div>
        </ModalShell>
      )}
    </div>
  );
}

